import React, { useState } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TableSortLabel,
    TextField,
    Button,
    CircularProgress,
    Alert,
    Box,
} from '@mui/material';
import { callApi } from '../utils/apiHelper';
import { useAuthenticator } from '@aws-amplify/ui-react';

function ResultsTable({ mode, onRowSelect = () => { } }) {
    const [jobs, setJobs] = useState([]);
    const [orderBy, setOrderBy] = useState('LastUpdatedAt');
    const [order, setOrder] = useState('desc');
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const { user } = useAuthenticator((context) => [context.user]);

    const fetchJobStatuses = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await callApi({
                user,
                path: '/ts-service-main/get_jobs',
                queryParams: {
                    excl_job_type: 'fit'
                },
                method: 'GET'
            });
            setJobs(response);
        } catch (error) {
            console.error('Error fetching job statuses:', error);
            setError(error.message || 'Failed to fetch job statuses');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const sortedJobs = [...jobs].sort((a, b) => {
        const compare = (a, b) => {
            if (a < b) return -1;
            if (a > b) return 1;
            return 0;
        };

        const modifier = order === 'asc' ? 1 : -1;
        return modifier * compare(a[orderBy], b[orderBy]);
    });

    const filteredJobs = sortedJobs.filter(job =>
        Object.values(job).some(value =>
            value.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
    );

    return (
        <Paper sx={{ padding: 2 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    marginBottom: 2
                }}
            >
                <Button
                    variant="contained"
                    onClick={fetchJobStatuses}
                    disabled={isLoading}
                >
                    {isLoading ? <CircularProgress size={24} /> : 'Fetch Completed Jobs'}
                </Button>

                <TextField
                    label="Search Jobs"
                    variant="outlined"
                    sx={{ flexGrow: 1 }}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </Box>

            {error && (
                <Alert severity="error" sx={{ marginBottom: 2 }}>
                    {error}
                </Alert>
            )}

            <TableContainer sx={{ maxHeight: 800 }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            {['JobType', 'JobLabel', 'LastUpdatedAt'].map((header) => (
                                <TableCell key={header}>
                                    <TableSortLabel
                                        active={orderBy === header}
                                        direction={orderBy === header ? order : 'asc'}
                                        onClick={() => handleSort(header)}
                                    >
                                        {header}
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredJobs.map((job) => (
                            <TableRow
                                key={job.JobID}
                                hover
                                onClick={() => onRowSelect(job)}
                                sx={{ cursor: 'pointer' }}
                            >
                                <TableCell>{job.JobType}</TableCell>
                                <TableCell>{job.JobLabel || ''}</TableCell>
                                <TableCell>
                                    {new Date(job.LastUpdatedAt).toLocaleString('en-US', {
                                        year: 'numeric',
                                        month: '2-digit',
                                        day: '2-digit',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        second: '2-digit',
                                        hour12: false
                                    })}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
}

export default ResultsTable;
